import { BotSequenceEnrollmentInterface, BotSequenceScheduledAttemptInterface, BotSequenceTestRunTrace, BotSequenceType } from "@swivl-mono/types";

/** Same horizon the scheduler enforces (SEQUENCE_STEP_MAX_STALENESS_DAYS). */
const MAX_STALENESS_DAYS = 7;

/** What each rule actually requires, so a non-enrolling subject points at the right field. */
const NON_QUALIFYING_DETAIL: Record<BotSequenceType, string> = {
  [BotSequenceType.PAST_DUE]:
    "The lease does not qualify — check it is active, that paidThroughDate is genuinely past, and that the tenant does not have disablePastDue set.",
  [BotSequenceType.ABANDONED_CART]: "The lead does not qualify — check its origination and status.",
  [BotSequenceType.MOVE_IN]: "The lease does not qualify — MOVE_IN needs an ACTIVE lease with a moveInDate that is not in the future.",
  [BotSequenceType.MOVE_OUT]:
    "The lease does not qualify — MOVE_OUT needs a moveOutDate that has arrived; a future move-out is a foreseen crossing, not membership."
};

export type DiagnosisTone = "pass" | "pending" | "warn" | "fail";

/** A one-click way out of the diagnosed state, when one exists. */
export interface RunDiagnosisAction {
  kind: "close_enrollment";
  enrollmentId: number;
  label: string;
}

export interface RunDiagnosis {
  tone: DiagnosisTone;
  /** One line stating the outcome. */
  headline: string;
  /** Why it happened, in the engine's own terms. */
  detail?: string;
  /** The operator's next move, when there is one. */
  nextStep?: string;
  action?: RunDiagnosisAction;
}

function daysBetweenIsoDates(fromIso: string, to: Date): number {
  const from = new Date(`${fromIso}T00:00:00Z`);
  const today = Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate());
  return Math.round((today - from.getTime()) / (24 * 60 * 60 * 1000));
}

/**
 * Interpret an attempt that has stopped moving. Every terminal status the scheduler or
 * handler can write is covered — a bare status string tells an operator nothing about
 * whether the run proved what it set out to prove.
 */
function diagnoseTerminal(attempt: BotSequenceScheduledAttemptInterface): RunDiagnosis | null {
  const id = `#${attempt.id}`;
  const metadata = attempt.metadata ?? {};

  if (attempt.status === "fired") {
    if (metadata.simulatedSend) {
      return {
        tone: "pass",
        headline: `PASS — attempt ${id} reached 'fired' via a simulated send.`,
        detail: "The whole pipeline ran on real rows; only the FMS data, the location config and the outward send were faked."
      };
    }
    // The bot really ran. What left Swivl is decided per delivery by the Outbound Gate (ENG-3373).
    const deliveries = Object.values(metadata.deliveries ?? {});
    const realCount = deliveries.filter((d) => d.status === "sent").length;
    const simulatedCount = deliveries.filter((d) => d.status === "simulated").length;
    if (deliveries.length > 0 && realCount === 0) {
      return {
        tone: "pass",
        headline: metadata.simulatedMode
          ? `Attempt ${id} fired as a Simulated-mode dry run — every delivery withheld.`
          : `Attempt ${id} fired — every delivery was simulated by the Outbound Gate.`,
        detail: metadata.simulatedMode
          ? "The location's mode is SIMULATED: the bot built each message for real and the Outbound Gate withheld all of them (reason sequence_simulated). Nothing left Swivl."
          : "The bot built each message for real; the gate withheld all of them (stream flag off, or recipient not on the allowlist). Nothing left Swivl. Each row's reason is in the Deliveries column."
      };
    }
    return {
      tone: "warn",
      headline: `Attempt ${id} fired with ${realCount} REAL ${realCount === 1 ? "delivery" : "deliveries"}${simulatedCount ? ` and ${simulatedCount} simulated` : ""}.`,
      detail: "simulateSend was off and the Outbound Gate let something go out — check the Deliveries column for who received what."
    };
  }

  if (attempt.status === "cancelled") {
    return {
      tone: "warn",
      headline: `Attempt ${id} was cancelled at fire time.`,
      detail: metadata.cancelReason
        ? `Reason: ${metadata.cancelReason}.`
        : "The handler's recheck rejected it — the enrollment closed, was suppressed, or the config turned off between booking and firing.",
      nextStep:
        metadata.cancelReason === "enrollment_closed"
          ? "If this run carried a jitDiff that resolved the obligation, this is the expected outcome."
          : undefined
    };
  }

  if (attempt.status === "failed") {
    return {
      tone: "fail",
      headline: `Attempt ${id} failed during the send.`,
      detail: metadata.failureReason ? `Channel error: ${metadata.failureReason}.` : "The send was attempted and errored."
    };
  }

  if (attempt.status === "simulated") {
    return {
      tone: "warn",
      headline: `Attempt ${id} is a legacy terminal 'simulated' row.`,
      detail:
        "Before 2026-09-23 SIMULATED mode booked terminal rows the dispatcher never claimed. Simulated mode now runs the day as a dry run, so a new run books a 'scheduled' row instead.",
      nextStep: "Start a new run — it will dispatch and fire with every delivery withheld."
    };
  }

  if (attempt.status === "skipped:stale") {
    if (metadata.staleReason === "before_enrollment") {
      return {
        tone: "warn",
        headline: `Day ${attempt.stepOffset}${Array.isArray(metadata.missedOffsets) && metadata.missedOffsets.length > 1 ? ` (and Days ${metadata.missedOffsets.filter((offset) => offset !== attempt.stepOffset).join(", ")})` : ""} was already past when the enrollment was created — missed, never fires.`,
        detail:
          "Decision 69: a sequence only sends the days that come due after the tenant is enrolled. Turning a sequence on never releases a backlog, 'Always send on this day' included.",
        nextStep: "Set the anchor so a ladder day lands on today, or wait for the next ladder day."
      };
    }
    return {
      tone: "warn",
      headline: `Step ${attempt.stepOffset} was skipped as stale.`,
      detail: `The step came due more than ${MAX_STALENESS_DAYS} days ago, so it will never fire.`,
      nextStep: "Move the anchor closer to today, or add a later step to the ladder."
    };
  }

  if (attempt.status === "skipped:holiday") {
    return {
      tone: "warn",
      headline: `Step ${attempt.stepOffset} was skipped — the local day is not fireable.`,
      detail: "A weekend, holiday or blackout day. This gate is deliberately NOT bypassed by a test run.",
      nextStep: "Run the scenario on a business day."
    };
  }

  if (attempt.status === "skipped:suppressed") {
    return {
      tone: "warn",
      headline: `Step ${attempt.stepOffset} was skipped — the enrollment is suppressed.`,
      detail: "An agent hold (suppressUntil) is in effect. The step stays due and will book once the hold expires.",
      nextStep: "Clear the suppression on the enrollment, or pick another subject."
    };
  }

  return null;
}

/** Interpret an attempt still moving through the pipeline. */
function diagnoseInFlight(attempt: BotSequenceScheduledAttemptInterface): RunDiagnosis | null {
  const id = `#${attempt.id}`;

  if (attempt.status === "scheduled") {
    return {
      tone: "pending",
      headline: `Attempt ${id} is booked and waiting for the dispatcher.`,
      detail: `Step ${attempt.stepOffset}, fires at ${attempt.fireAt ? new Date(attempt.fireAt).toLocaleTimeString() : "an unset time"}.`,
      nextStep: "Press Force dispatch tick — or wait for the regular tick (up to ~3 min)."
    };
  }

  if (attempt.status === "dispatched") {
    return {
      tone: "pending",
      headline: `Attempt ${id} was claimed by the dispatcher.`,
      detail: "The fire job is on the queue; the handler has not finished yet.",
      nextStep: "If it sits here, the sequence worker is not consuming the fire queue (BULL_WORKER_SCOPE)."
    };
  }

  if (attempt.status === "sending") {
    return {
      tone: "pending",
      headline: `Attempt ${id} is mid-send.`,
      detail: "The write-ahead 'sending' row is set, so the outcome is not yet known. It is never auto-resent.",
      nextStep: "If it stays here, the stale-sending sweeper resolves it on evidence after ~30 min."
    };
  }

  return null;
}

/**
 * Why no attempt exists yet. This is the case that looks like a hang but usually is not —
 * the scheduler's "nothing due" is a silent no-op that writes no row at all, so without
 * this the page can only say "waiting" forever.
 */
function diagnoseNoAttempt(params: {
  trace: BotSequenceTestRunTrace;
  enrollment: BotSequenceEnrollmentInterface | null;
  ladderOffsetDays: number[];
  now: Date;
}): RunDiagnosis {
  const { trace, enrollment, ladderOffsetDays, now } = params;

  if (!enrollment) {
    if (trace.reconcileRuns.length === 0) {
      return {
        tone: "pending",
        headline: "Waiting for the reconciler.",
        detail: "The outbox row is planted; the drain picks it up on its own ~30s tick.",
        nextStep: "If nothing happens within a minute, the sequence worker is not running (BULL_WORKER_SCOPE=all)."
      };
    }
    return {
      tone: "warn",
      headline: "The reconciler ran but did not enroll the subject.",
      detail: NON_QUALIFYING_DETAIL[trace.run.sequenceType],
      nextStep: "Adjust initialDiff so the subject qualifies, then start a new run."
    };
  }

  const sorted = [...ladderOffsetDays].sort((left, right) => left - right);
  const offsetToday = daysBetweenIsoDates(enrollment.anchorDate, now);
  const furthest = enrollment.furthestStepFired ?? null;
  const maxStep = sorted.length > 0 ? sorted[sorted.length - 1] : null;
  const minStep = sorted.length > 0 ? sorted[0] : null;

  if (maxStep !== null && furthest !== null && furthest >= maxStep) {
    return {
      tone: "warn",
      headline: "The ladder is already fully fired for this anchor.",
      detail: `Enrollment #${enrollment.id} (anchor ${enrollment.anchorDate}) has fired up to step ${furthest}, and the ladder ends at ${maxStep}. No step is due, so the scheduler books nothing — this is a silent no-op, not a failure.`,
      nextStep:
        "Close the episode below and the reconciler opens a fresh one on the next drain. Or change paidThroughDate to move the anchor, or add a later step.",
      action: { kind: "close_enrollment", enrollmentId: enrollment.id, label: `Close enrollment #${enrollment.id} and re-reconcile` }
    };
  }

  if (minStep !== null && offsetToday < minStep) {
    return {
      tone: "warn",
      headline: "No step is due yet.",
      detail: `Day 0 is ${enrollment.anchorDate}, today is offset ${offsetToday}, and the earliest step is ${minStep}.`,
      nextStep: "Move the anchor further into the past, or add an earlier step to the ladder."
    };
  }

  // Decision 69: days before the enrollment existed are missed; a fresh enrollment only fires a
  // ladder day that lands on today or later.
  if (!sorted.includes(offsetToday)) {
    const next = sorted.find((step) => step > offsetToday);
    return {
      tone: "warn",
      headline: "No ladder day lands on today, and the earlier ones were missed.",
      detail: `Day 0 is ${enrollment.anchorDate}, today is offset ${offsetToday}. Days before the enrollment was created never fire (decision 69).${next !== undefined ? ` Day ${next} is next.` : ""}`,
      nextStep: "Set the anchor so a ladder day lands on today."
    };
  }

  return {
    tone: "pending",
    headline: "Enrolled — waiting for the scheduler to book.",
    detail: `Enrollment #${enrollment.id}, anchor ${enrollment.anchorDate}, today is offset ${offsetToday}.`,
    nextStep: "The reconciler's book request runs immediately; if nothing appears, check the org's bot_sequence_engine_scheduler flag."
  };
}

/**
 * The single verdict shown under a run. Reads only this run's attempts (an earlier episode's
 * `fired` row must never be mistaken for this run's success) and falls back to explaining
 * why nothing has booked.
 */
export function diagnoseRun(params: {
  trace: BotSequenceTestRunTrace;
  runAttempts: BotSequenceScheduledAttemptInterface[];
  ladderOffsetDays: number[];
  now?: Date;
}): RunDiagnosis {
  const { trace, runAttempts, ladderOffsetDays } = params;
  const now = params.now ?? new Date();

  // `skipped:superseded` rows are bookkeeping for steps that collapsed into a later one —
  // never the outcome of the run.
  const meaningful = runAttempts.filter((attempt) => attempt.status !== "skipped:superseded");

  for (const attempt of meaningful) {
    const terminal = diagnoseTerminal(attempt);
    if (terminal) {
      return terminal;
    }
  }
  for (const attempt of meaningful) {
    const inFlight = diagnoseInFlight(attempt);
    if (inFlight) {
      return inFlight;
    }
  }

  const enrollment =
    trace.enrollments.find((candidate) => candidate.id === trace.run.enrollmentId) ??
    trace.enrollments.find((candidate) => candidate.status === "ACTIVE" && candidate.sequenceType === trace.run.sequenceType) ??
    null;

  return diagnoseNoAttempt({ trace, enrollment, ladderOffsetDays, now });
}
